<template>
  <div class="container-fluid py-1">
    <div class="row">
      <div class="col-12">
        <div
          class="card my-4"
          style="min-height: 500px; display: flex; flex-direction: column"
        >
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div
              class="bg-gradient-success shadow-success border-radius-lg pt-2 pb-3"
            >
              <h6 class="text-white text-capitalize ps-3">System Audit Logs</h6>
              <p class="text-white text-sm ps-3 mb-0">
                Track all system activities and user actions
              </p>
            </div>
          </div>

          <div class="card-body px-0 pb-2" style="flex: 1">
            <div class="table-responsive p-0">
              <!-- Loading  -->
              <div v-if="isLoading" class="text-center py-5">
                <div class="spinner-border text-success" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-3 text-muted">Loading audit logs...</p>
              </div>

              <!-- Error State -->
              <div v-else-if="hasError" class="text-center py-5">
                <i
                  class="fas fa-exclamation-triangle fa-3x text-warning mb-3"
                ></i>
                <h5 class="text-warning">Failed to load audit logs</h5>
                <p class="text-muted">Please try again later.</p>
                <material-button
                  color="success"
                  size="sm"
                  @click="loadAuditLogs"
                >
                  <i class="fas fa-redo me-2"></i>
                  Retry
                </material-button>
              </div>

              <!-- Empty State -->
              <div
                v-else-if="!auditLogs || auditLogs.length === 0"
                class="text-center py-5"
              >
                <i class="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No audit logs found</h5>
                <p class="text-muted">No activities recorded yet</p>
              </div>

              <!-- Table -->
              <table v-else class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase">#</th>
                    <th class="text-uppercase">User</th>
                    <th class="text-uppercase">Action</th>
                    <th class="text-uppercase">Details</th>
                    <th class="text-uppercase">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(log, index) in paginatedAuditLogs"
                    :key="log.id || index"
                    class="table-row-hover"
                  >
                    <!-- Index Column -->
                    <td class="ps-4">
                      <p class="text-xs font-weight-bold mb-0">
                        {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                      </p>
                    </td>

                    <!-- User Column -->
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="user-icon">
                          <i class="fas fa-user"></i>
                        </div>
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">
                            {{ log.user || "Unknown User" }}
                          </h6>
                        </div>
                      </div>
                    </td>

                    <!-- Action Column -->
                    <td>
                      <span
                        :class="getActionBadgeClass(log.action)"
                        class="badge badge-sm"
                      >
                        {{ log.action || "Unknown Action" }}
                      </span>
                    </td>

                    <!-- Details Column -->
                    <td>
                      <p class="text-sm font-weight-bold mb-0">
                        {{ log.details || "No details provided" }}
                      </p>
                    </td>

                    <!-- Timestamp Column -->
                    <td>
                      <p class="text-sm font-weight-bold mb-0">
                        {{ formatDate(log.created_at) }}
                      </p>
                      <p class="text-xs text-secondary mb-0">
                        {{ formatTime(log.created_at) }}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Card Footer - Always shown -->
          <div class="card-footer px-3" style="margin-top: auto">
            <div v-if="auditLogs && auditLogs.length > 0">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="mb-0 text-sm text-secondary">
                    Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                    {{ Math.min(currentPage * itemsPerPage, auditLogs.length) }}
                    of {{ auditLogs.length }} entries
                  </p>
                </div>

                <!-- Material Pagination -->
                <material-pagination :color="'success'" :size="'sm'">
                  <material-pagination-item
                    :prev="true"
                    :disabled="currentPage === 1"
                    @click="changePage(currentPage - 1)"
                  />

                  <material-pagination-item
                    v-for="page in displayedPages"
                    :key="page"
                    :label="page.toString()"
                    :active="page === currentPage"
                    @click="changePage(page)"
                  />

                  <material-pagination-item
                    :next="true"
                    :disabled="currentPage === totalPages"
                    @click="changePage(currentPage + 1)"
                  />
                </material-pagination>
              </div>
            </div>
            <div v-else class="text-center text-muted">
              <small>No records to display</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialPagination from "@/components/MaterialPagination.vue";
import MaterialPaginationItem from "@/components/MaterialPaginationItem.vue";
import axios from "axios";

export default {
  name: "audits",
  components: {
    MaterialButton,
    MaterialPagination,
    MaterialPaginationItem,
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      auditLogs: [],

      // Pagination
      currentPage: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.auditLogs.length / this.itemsPerPage);
    },

    displayedPages() {
      const pages = [];
      const maxVisible = 5;

      if (this.totalPages <= 0) return pages;

      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    paginatedAuditLogs() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.auditLogs.slice(start, end);
    },
  },
  created() {
    this.loadAuditLogs();
  },
  methods: {
    async loadAuditLogs() {
      this.isLoading = true;
      this.hasError = false;

      try {
        const response = await axios.get("/audit-trails/");

        console.log("Audit logs response:", response.data);

        if (response.data && Array.isArray(response.data)) {
          this.auditLogs = response.data;
          this.auditLogs.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at),
          );
        } else {
          console.error("Unexpected response format:", response.data);
          this.auditLogs = [];
        }
      } catch (error) {
        console.error("Error loading audit logs:", error);
        this.hasError = true;
        this.auditLogs = [];

        this.showNotification("Failed to load audit logs", "error");
      } finally {
        this.isLoading = false;
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },

    // Helper Methods
    getEmailFromUser(userString) {
      if (userString && userString.includes("@")) {
        return userString;
      }
      return "";
    },

    getActionBadgeClass(action) {
      const classMap = {
        "User Login": "badge bg-gradient-success",
        "User Logout": "badge bg-gradient-secondary",
        "File Upload": "badge bg-gradient-info",
        "File Process": "badge bg-gradient-primary",
        "URP Sent": "badge bg-gradient-warning",
        "URP Committed": "badge bg-gradient-success",
        "User Created": "badge bg-gradient-info",
        "User Updated": "badge bg-gradient-warning",
        "User Deleted": "badge bg-gradient-danger",
        System: "badge bg-gradient-dark",
      };
      return classMap[action] || "badge bg-gradient-secondary";
    },

    formatDate(dateTime) {
      if (!dateTime) return "N/A";
      try {
        const date = new Date(dateTime);
        if (isNaN(date.getTime())) return "Invalid Date";
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        console.warn("Error formatting date:", dateTime, error);
        return "Invalid Date";
      }
    },

    formatTime(dateTime) {
      if (!dateTime) return "";
      try {
        const date = new Date(dateTime);
        if (isNaN(date.getTime())) return "";
        return date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      } catch (error) {
        console.warn("Error formatting time:", dateTime, error);
        return "";
      }
    },

    showNotification(message, type = "info") {
      const alertClass =
        {
          success: "alert-success",
          error: "alert-danger",
          warning: "alert-warning",
          info: "alert-info",
        }[type] || "alert-info";

      const notification = document.createElement("div");
      notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      notification.style.cssText =
        "top: 20px; right: 20px; z-index: 9999; min-width: 300px;";
      notification.innerHTML = `
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.remove();
      }, 3000);
    },
  },
};
</script>

<style scoped>
.container-fluid {
  min-height: calc(100vh - 60px);
}

.card {
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.table-responsive {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.user-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-right: 12px;
  background-color: #f8f9fa;
  border-radius: 50%;
  color: #6c757d;
}

.text-center.py-5 {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.badge {
  font-size: 0.65em;
  font-weight: 600;
  padding: 0.25em 0.6em;
}

.bg-gradient-success {
  background: linear-gradient(310deg, #f44335 0%, #ef5350 100%);
}

.bg-gradient-info {
  background: linear-gradient(310deg, #17a2b8 0%, #3dcfd3 100%);
}

.bg-gradient-warning {
  background: linear-gradient(310deg, #fbd38d 0%, #f59e0b 100%);
}

.bg-gradient-danger {
  background: linear-gradient(310deg, #f44336 0%, #f88a82 100%);
}

.bg-gradient-primary {
  background: linear-gradient(310deg, #2196f3 0%, #64b5f6 100%);
}

.bg-gradient-secondary {
  background: linear-gradient(310deg, #6c757d 0%, #adb5bd 100%);
}

.bg-gradient-dark {
  background: linear-gradient(310deg, #343a40 0%, #6c757d 100%);
}

.table-row-hover {
  transition: all 0.2s ease;
}

.table-row-hover:hover {
  background-color: rgba(244, 67, 53, 0.05);
  transform: scale(1.002);
}

.card-footer {
  margin-top: auto;
  background-color: transparent;
  border-top: 1px solid #e9ecef;
  padding: 1rem 0;
}

.shadow-success {
  box-shadow:
    0 4px 20px 0 rgba(244, 67, 53, 0.14),
    0 7px 10px -5px rgba(239, 83, 80, 0.4);
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.875rem;
  }

  .user-icon {
    width: 28px;
    height: 28px;
    margin-right: 8px;
  }
}
</style>
